// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { MathUtils } from '@shared/utils/MathUtils';
import DPPositionSizingControlsTemplate from '@front/templates/DPPositionSizingControlsTemplate.html';
import { DynPropertyControl } from '../elements/DynPropertyControl';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class DPPositionSizingControls extends DynPropertyControl {
    constructor() {
        super();
        this.selectedAccRiskModeTag = null;
    }
    getType() {
        return 'DPPositionSizingControls';
    }
    oninit() {
        super.oninit();
        this.on('fixedOrPercentageBtnClick', this.fixedOrPercentageBtnClick);
        this.on('fixedOrPercentageBtnMouseDown', this.fixedOrPercentageBtnMouseDown);
    }
    oncomplete() {
        super.oncomplete();
        this.selectAccRiskMode(DPPositionSizingControls.PercentTag, true);
        this.localize();
    }
    selectAccRiskMode(tag, fromOnComplete) {
        if (this.selectedAccRiskModeTag === tag) {
            return;
        }
        this.selectedAccRiskModeTag = tag;
        const accCur = this.get('Currency');
        const isPercent = this.isPercentMode();
        const valueToShowInBtn = isPercent ? '%' : accCur;
        if (!fromOnComplete) {
            const newV = this.get('dp.riskValue');
            void this.set('AccountRisk', isPercent ? MathUtils.TruncateDouble(newV, 2) : newV);
        }
        void this.set('dp.riskModeBtnText', valueToShowInBtn);
        void this.set({
            riskModeBtnText: valueToShowInBtn,
            riskLabel: Resources.getResource('positionSizeCalculator.' + (isPercent ? 'cashRiskLabel' : 'percentRiskLabel'))
        });
    }
    fixedOrPercentageBtnMouseDown(context) {
        if (this.get('fixedOrPercentMenuIsOpen')) {
            context.original.stopPropagation();
        }
    }
    fixedOrPercentageBtnClick() {
        const t = this.get('fixedOrPercentMenuIsOpen');
        if (this.get('fixedOrPercentMenuIsOpen')) {
            hideContextMenu();
            return;
        }
        if (!this.get('enabled')) {
            return;
        }
        const items = this.getPercentCashItems();
        if (isValidArray(items)) {
            void this.set('fixedOrPercentMenuIsOpen', true);
            const button = this.find('.js-button-fixedOrPercentMode');
            const buttonRect = button.getBoundingClientRect();
            const closeCallback = () => {
                void this.set('fixedOrPercentMenuIsOpen', false);
            };
            showContextMenu({
                x: buttonRect.left,
                y: buttonRect.top + buttonRect.height,
                items: items,
                functions: { closeCallback: closeCallback }
            });
        }
    }
    getPercentCashItems() {
        const isPercentMode = this.isPercentMode();
        const items = [
            {
                type: NewContextMenuItemType.Default,
                text: 'positionSizeCalculator.FixedMenuItem',
                canCheck: true,
                checked: !isPercentMode,
                event: (item) => {
                    this.selectAccRiskMode(DPPositionSizingControls.FixedTag, false);
                }
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'positionSizeCalculator.PercentMenuItem',
                canCheck: true,
                checked: isPercentMode,
                event: (item) => {
                    this.selectAccRiskMode(DPPositionSizingControls.PercentTag, false);
                }
            }
        ];
        return items;
    }
    isPercentMode() {
        return this.selectedAccRiskModeTag === DPPositionSizingControls.PercentTag;
    }
    localize() {
        super.localize();
        void this.set({
            accountRiskLabel: Resources.getResource('positionSizeCalculator.accountRisk'),
            accountRiskTT: Resources.getResource('positionSizeCalculator.accountRisk.tt'),
            riskLabel: Resources.getResource('positionSizeCalculator.cashRiskLabel')
        });
    }
}
DPPositionSizingControls.PercentTag = 'percent';
DPPositionSizingControls.FixedTag = 'fixed';
DynPropertyControl.extendWith(DPPositionSizingControls, {
    template: DPPositionSizingControlsTemplate,
    data: function () {
        return {
            positionSizingIsVisible: false, // скрываем через эту переменную, а не visible потому что для корректной работы нужно чтобы контролы были созданы
            riskModeBtnText: '%', // текст в кнопке у нумерика (% или валюта)
            riskLabel: '', // подпись второй строки
            fixedOrPercentMenuIsOpen: false,
            showLabel: true,
            width: 176
        };
    },
    computed: {
        AccountRisk: {
            get: function () {
                return this.get('dp.value');
            },
            set: function (value) {
                void this.set('dp.value', value);
            }
        },
        OtherRisk: {
            get: function () {
                return this.get('dp.riskValueFormatted');
            },
            set: function (value) {
                void this.set('dp.riskValueFormatted', value);
            }
        },
        Currency: {
            get: function () {
                return this.get('dp.currencyRiskModeSuffix');
            },
            set: function (value) {
                void this.set('dp.currencyRiskModeSuffix', value);
            }
        }
    }
});
